import { guardianPlayerKeys } from '@/features/guardian-player/queries/query-keys';
import { guardianKeys } from '@/features/guardian/queries/query-keys';
import {
  addFavoriteCoaches,
  type AddFavoriteCoachesRequest,
} from '@/features/guardian/services/add-favorite-coaches';
import {
  createGuardianPlayer,
  type CreateGuardianPlayerRequest,
} from '@/features/guardian/services/create-guardian-player';
import { fetchGuardianMe } from '@/features/guardian/services/get-guardian';
import { fetchGuardianPlayers } from '@/features/guardian/services/get-guardian-players';
import {
  updateGuardianMe,
  type GuardianProfileUpdate,
} from '@/features/guardian/services/update-guardian';
import { deleteGuardianPlayer } from '@/features/guardian/services/delete-guardian-player';
import {
  updateGuardianPlayer,
  type UpdateGuardianPlayerRequest,
} from '@/features/guardian/services/update-guardian-player';
import {
  updateNotificationPreferences,
  type NotificationPreferencesUpdate,
} from '@/features/guardian/services/update-notification-preferences';
import {
  updateOnboardingProgress,
  type OnboardingProgressUpdate,
} from '@/features/guardian/services/update-onboarding';
import {
  dismissWelcomeProgress,
  saveWelcomeProgress,
  startWelcomeProgress,
} from '@/features/guardian/services/save-welcome-progress';
import { useSessionStore } from '@/stores';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/shallow';

/**
 * Signed-in guardian's profile from `GET /api/v1/guardians/me`.
 * Pass the auth `uid` to key the cache + gate the request on being signed in.
 */
export function useGuardianProfileQuery(guardianId?: string) {
  const roles = useSessionStore(useShallow(s => s.roles));
  return useQuery({
    queryKey: guardianKeys.profile(guardianId),
    queryFn: () => fetchGuardianMe(),
    enabled: Boolean(guardianId) && roles.includes('guardian'),
  });
}

/**
 * Players linked to the signed-in guardian from `GET /api/v1/guardians/me/players`
 * (matched by verified email). Pass the auth `uid` to key the cache + gate the
 * request on being signed in.
 */
export function useGuardianPlayersQuery(guardianId?: string) {
  return useQuery({
    queryKey: guardianKeys.players(guardianId),
    queryFn: () => fetchGuardianPlayers(),
    enabled: Boolean(guardianId),
  });
}

/**
 * Creates a player for the signed-in guardian via POST `/api/v1/guardians/me/players`,
 * then refetches the players list so the new golfer shows up. Pass the auth `uid`
 * so the invalidated key matches `useGuardianPlayersQuery`.
 */
export function useCreateGuardianPlayerMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGuardianPlayerRequest) => createGuardianPlayer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guardianKeys.players(guardianId) });
    },
  });
}

/**
 * Updates a player owned by the signed-in guardian via PATCH
 * `/api/v1/guardians/me/players/{playerId}`, then refetches the players list. Pass the auth
 * `uid` so the invalidated key matches `useGuardianPlayersQuery`.
 */
export function useUpdateGuardianPlayerMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playerId, data }: { playerId: string; data: UpdateGuardianPlayerRequest }) =>
      updateGuardianPlayer(playerId, data),
    onSuccess: (_result, { playerId }) => {
      queryClient.invalidateQueries({ queryKey: guardianKeys.players(guardianId) });
      // Refresh the Player Detail screen (reads by id) so it shows the updated data.
      queryClient.invalidateQueries({ queryKey: guardianPlayerKeys.playerDetail(playerId) });
    },
  });
}

/**
 * Deletes a player owned by the signed-in guardian via DELETE
 * `/api/v1/guardians/me/players/{playerId}`, then refetches the players list. Pass the auth
 * `uid` so the invalidated key matches `useGuardianPlayersQuery`.
 */
export function useDeleteGuardianPlayerMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (playerId: string) => deleteGuardianPlayer(playerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guardianKeys.players(guardianId) });
    },
  });
}

/**
 * Partial update of the signed-in guardian via PATCH `/api/v1/guardians/me`.
 * Seeds the returned profile into the cache so the next read is fresh; pass the
 * auth `uid` so the cache key matches `useGuardianProfileQuery`.
 */
export function useUpdateGuardianProfileMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GuardianProfileUpdate) => updateGuardianMe(data),
    onSuccess: profile => {
      queryClient.setQueryData(guardianKeys.profile(guardianId), profile);
    },
  });
}

/**
 * Saves the guardian's favorite coaches via POST `/api/v1/guardians/me/favorite-coaches`,
 * then invalidates the profile so its `favoriteCoaches` refetches. Pass the auth `uid`
 * so the invalidated key matches `useGuardianProfileQuery`.
 */
export function useAddFavoriteCoachesMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddFavoriteCoachesRequest) => addFavoriteCoaches(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guardianKeys.profile(guardianId) });
    },
  });
}

/**
 * Saves the guardian's notification opt-ins (onboarding step 4) via PATCH
 * `/api/v1/guardians/me/notification-preferences`. Seeds the returned profile into
 * the cache; pass the auth `uid` so the cache key matches `useGuardianProfileQuery`.
 */
export function useUpdateNotificationPreferencesMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: NotificationPreferencesUpdate) => updateNotificationPreferences(data),
    onSuccess: profile => {
      queryClient.setQueryData(guardianKeys.profile(guardianId), profile);
    },
  });
}

/**
 * Records an onboarding step outcome via PATCH `/api/v1/guardians/me/onboarding`
 * (the endpoint returns the refreshed profile, which we seed into the cache).
 */
export function useUpdateOnboardingProgressMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OnboardingProgressUpdate) => updateOnboardingProgress(data),
    onSuccess: profile => {
      queryClient.setQueryData(guardianKeys.profile(guardianId), profile);
    },
  });
}

/**
 * Marks a "Get Started" guide step done via PATCH `/api/v1/guardians/me/getting-started`.
 * The endpoint returns the refreshed profile (with the updated `gettingStarted` block), seeded
 * into the profile cache so the Home Welcome flow re-derives.
 */
export function useUpdateGettingStartedMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (step: number) => saveWelcomeProgress(step),
    onSuccess: profile => {
      queryClient.setQueryData(guardianKeys.profile(guardianId), profile);
    },
  });
}

/** Starts the "Get Started" guide (idempotent POST); seeds the refreshed profile into the cache. */
export function useStartGettingStartedMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => startWelcomeProgress(),
    onSuccess: profile => {
      queryClient.setQueryData(guardianKeys.profile(guardianId), profile);
    },
  });
}

/** Dismisses the "Get Started" guide (close early); seeds the refreshed profile into the cache. */
export function useDismissGettingStartedMutation(guardianId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => dismissWelcomeProgress(),
    onSuccess: profile => {
      queryClient.setQueryData(guardianKeys.profile(guardianId), profile);
    },
  });
}
